import React, {useEffect, useMemo, useState} from "react";
import {PaymentElement, useElements, useStripe} from "@stripe/react-stripe-js";
import {useDispatch, useSelector} from "react-redux";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCircleXmark} from "@fortawesome/pro-regular-svg-icons/faCircleXmark";

import Alert from "@/components/Global/Alert/Alert";
import Box from "@/components/Global/Box/Box";
import Button from "@/components/Global/Button/Button";
import RenderIf from "@/components/Global/RenderIf/RenderIf";

import {addToBasket, loadBasket, updatePaymentIntent} from "@/features/basket";
import {loadPreBookOrders, updatePreBookOrders} from "@/features/orders";
import {updateCollectionMethod, updateNextDispatch} from "@/features/subscriptions";
import {base64Encrypt} from "@/helpers/base64Encrypt.js";
import {formatNumber} from "@/helpers/formatNumber.js";
import {generateOrderCookieId} from "@/helpers/generateOrderCookieId.js";
import {useCookies} from "@/hooks/useCookies/useCookies";
import {useCountdown} from "@/hooks/useCountdown/useCountdown.js";
import {useIP} from "@/hooks/useIP/useIP.js";

import {emojis} from "@/constants/emojis.js";
import {config} from "@/config";

import styles from "./PaymentForm.module.scss";
import {selectPreBookSettings} from "@/features/appSettings/appSettingsSelectors";
import {selectUserDetails} from "@/features/user/userSelectors";
import {selectPreBookOrdersData} from "@/features/orders/ordersSelector";

const SECONDS_TO_REDIRECT = 5;

export default function PaymentForm({
  action = "pre-book",
  handleGoTo,
  handleBack,
  handleCloseModal,
  dispatchDate,
  testPrice,
  testId,
  stripeId = "",
  collectionMethod,
  newCollectionMethod = {},
  order = {},
  newDispatchDate,
  loadStripeDetails,
  clientSecret,
}) {
  const userDetails = useSelector(selectUserDetails);
  const preBookDetails = useSelector(selectPreBookSettings);
  const preBookOrders = useSelector(selectPreBookOrdersData);
  const [isDisabled, setIsDisabled] = useState(true);
  const [isSuccessfully, setIsSuccessfully] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paymentMethodType, setPaymentMethodType] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showError, setShowError] = useState(false);

  const stripeTestAmount = useMemo(() => parseFloat((testPrice * 100).toFixed(2)), [testPrice]);
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();
  const {ip} = useIP();
  const {getCookie, setCookie} = useCookies();
  const {secondsRemaining, activateCountdown} = useCountdown(SECONDS_TO_REDIRECT);

  useEffect(() => {
    if (stripe) {
      const paymentRequestStripe = stripe.paymentRequest({
        country: "GB",
        currency: "gbp",
        total: {
          label: "Forth",
          amount: stripeTestAmount,
        },
      });

      paymentRequestStripe.on("paymentmethod", e => {
        const isAppleGooglePay = 1;
        handleSubmit(e, isAppleGooglePay);
      });
    }
  }, [stripe, stripeTestAmount]);

  const handleDisabledButton = e => {
    if (e.value.type) setPaymentMethodType(e.value.type);
    if (e.complete) return setIsDisabled(false);

    setIsDisabled(true);
  };

  const getOrderCookieId = () => {
    let orderCookieId = localStorage.getItem("orderCookieId");
    if (!orderCookieId) {
      orderCookieId = getCookie("orderCookieId");
    }

    return orderCookieId;
  };

  const handleSubmit = async (e, isAppleGooglePay = 0) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (action === "pre-book") {
      // 1: Add to the basket to create the order and get the basket
      const addToBasket = await addTestToTheBasket();
      if (addToBasket.error) {
        setShowError(true);
        setIsSubmitting(false);
      }

      const basket = await getBasket();
      if (basket.error) {
        setShowError(true);
        setIsSubmitting(false);
      }

      // 2: Create the subscription
      const {orderId, totalAmount, totalSaving} = basket;
      await buySubscription({isAppleGooglePay, orderId, totalAmount, totalSaving});
    }

    if (action === "updateDispatchDate") {
      updateDispatchDate();
    }

    if (action === "changeCollectionMethod") {
      updateMethod();
    }
  };

  const addTestToTheBasket = () => {
    const orderCookieId = generateOrderCookieId();

    if (orderCookieId) {
      setCookie({name: "orderCookieId", value: orderCookieId});
      localStorage.setItem("orderCookieId", orderCookieId);
    }

    const payload = {
      testId,
      orderCookieId,
      customerId: userDetails.ID,
      purchaseGroup: 1,
      forthEdge: config.websiteId == 2 ? 1 : 0,
      phlebotomy: collectionMethod,
      deliveryOption: 0,
      update: 0,
      partnerId: userDetails?.partnerId || 0,
      voucherCode: preBookDetails?.voucherDetails?.voucherCode,
      preBook: true,
    };

    return dispatch(addToBasket(payload));
  };

  const getBasket = async () => {
    const orderCookieId = getOrderCookieId();
    const response = await dispatch(loadBasket({orderCookieId}));
    const [basket, status] = response?.payload || [];
    // eslint-disable-next-line prefer-promise-reject-errors
    if (status !== 201) return Promise.reject({error: true, message: "Something went wrong while loading the basket"});

    const {totalAmount, totalSaving, orderId} = basket;

    const [
      testId,
      testName,
      testType,
      testPrice,
      testPriceWithDiscount,
      billingFrequency,
      testBundleQuantity,
      phlebotomy,
      postPKit,
      isBespokeTest,
      phlebotomyKitCode,
      voucherPhlebotomyOnly,
      phlebotomyClinic,
      isNextDayDelivery,
      fulfillmentId,
      isDoubleKit,
      isSubscriptionAvailable,
      isNonCustomisable,
      // eslint-disable-next-line
      testPrice_sas,
      // eslint-disable-next-line
      testPrice_sasSaving,
      originalPrice,
      originalOfferPrice,
      homePhlebotomyOnly,
      preventGuestPurchase,
    ] = basket.basketArray[0];

    return Promise.resolve({
      totalAmount,
      orderId,
      testId,
      testName,
      testType,
      testPrice,
      testPriceWithDiscount,
      totalSaving,
      billingFrequency,
      testBundleQuantity,
      phlebotomy,
      postPKit,
      isBespokeTest,
      phlebotomyKitCode,
      voucherPhlebotomyOnly,
      phlebotomyClinic,
      isNextDayDelivery,
      fulfillmentId,
      isDoubleKit,
      isSubscriptionAvailable,
      isNonCustomisable,
      // eslint-disable-next-line
      testPriceSas: testPrice_sas,
      // eslint-disable-next-line
      testPriceSasSaving: testPrice_sasSaving,
      originalPrice,
      originalOfferPrice,
      homePhlebotomyOnly,
      preventGuestPurchase,
    });
  };

  const buySubscription = async ({isAppleGooglePay = 0, orderId, totalAmount, totalSaving}) => {
    const payload = {
      userId: userDetails.ID,
      userIp: ip,
      websiteId: config.websiteId,
      amount: stripeTestAmount,
      id: stripeId,
      orderReference: "",
      orderId,
      stripeCustomerId: userDetails.customerStripeId,
      userEmail: userDetails.customerEmailAddress,
      totalAmount,
      totalSaving,
      subscriptionOrder: "true",
      activationKit: false,
      partnerId: userDetails?.partnerId || 0,
      partnerCode: "",
      overidePayPalProcess: "",
      voucherId: preBookDetails?.voucherDetails?.voucherId,
      f: "",
      freeOrderEncryptedUrl: "",
      orderCookieId: getOrderCookieId(),
      doNotReview: userDetails?.doNotReview,
      credit: 0,
      discountedCredit: 0,
      receiptEmailAddress: userDetails.customerEmailAddress,
      grantPermissionToPartner: 0,
      checkoutFromPartner: 0,
      clientEmailsCheck: false,
      clientReciept: "",
      dispatchDate,
      preBook: true,
    };

    const payloadWindow = base64Encrypt(payload);

    if (isAppleGooglePay == 0) {
      stripe
        .confirmSetup({
          elements,
          confirmParams: {
            // eslint-disable-next-line camelcase
            return_url: `${window.location.origin}/completion/${payloadWindow}`,
          },
          redirect: "if_required",
        })
        .then(response => {
          if (response.error) {
            setIsSubmitting(false);
            setShowError(true);
          } else {
            return dispatch(updatePaymentIntent(payload)).then(({payload: response}) => {
              if (response.error) {
                setShowError(true);
                setIsSubmitting(false);
                return;
              }

              setIsSuccessfully(true);
              setIsSubmitting(false);
              setSuccessMessage("Your order has been pre-booked successfully");
              activateCountdown();

              setTimeout(() => {
                dispatch(loadPreBookOrders("me"));
                setIsSuccessfully(false);
                handleCloseModal && handleCloseModal();
              }, SECONDS_TO_REDIRECT * 1000);
            });
          }
        });
    }
  };

  const confirmSetup = async () => {
    const response = await stripe.confirmSetup({
      elements,
      confirmParams: {
        // eslint-disable-next-line camelcase
        return_url: `${window.location.origin}/my-account/order-history`,
      },
      redirect: "if_required",
    });

    const {error = "", setupIntent = {}} = response;

    return {error, paymentMethod: setupIntent.payment_method};
  };

  const updateDispatchDate = async () => {
    const {error, paymentMethod: stripeToken} = await confirmSetup();

    if (!error) {
      const payload = {
        stripeCustomerId: userDetails.customerStripeId,
        stripeToken,
        subscriptionId: order.subscriptionId,
        subscriptionType: "stripe",
        subscriptionStatus: "newDateFrequency",
        newDispatchDate,
      };

      dispatch(updateNextDispatch(payload)).then(({payload: response}) => {
        if (response.error) {
          setShowError(true);
          setIsSubmitting(false);
          return;
        }

        setIsSuccessfully(true);
        setIsSubmitting(false);
        setSuccessMessage("Your order has been updated successfully");
        activateCountdown();

        const newPreBookArray = preBookOrders.map(preBook => {
          if (preBook.subscriptionId == order.subscriptionId) {
            return {...preBook, nextDispatch: newDispatchDate};
          }
          return preBook;
        });

        setTimeout(() => {
          handleCloseModal && handleCloseModal();
          loadStripeDetails && loadStripeDetails();
          setIsSuccessfully(false);
          dispatch(updatePreBookOrders(newPreBookArray));
        }, SECONDS_TO_REDIRECT * 1000);
      });
    } else {
      setShowError(true);
      setIsSubmitting(false);
    }
  };

  const updateMethod = async () => {
    const {error, paymentMethod: stripeToken} = await confirmSetup();

    if (!error) {
      const {subscriptionId, phlebotomy, testType, postPKit, phlebotomyKitCode, kitStatus} = order;
      const phlebotomyIdSelected = newCollectionMethod.phlebotomyId;

      const subscriptionStatus = {
        1: "fingerPrickKit",
        2: "phlebotomyAppointment",
        3: "phlebotomyKit",
        5: "phlebotomyClinic",
      };

      if (phlebotomyKitCode !== "KT257") {
        if (phlebotomy == 0 && phlebotomyKitCode !== "") {
          subscriptionStatus[3] = "phlebotomyKitF";
        }

        if (phlebotomy == 1 && postPKit == 0) {
          subscriptionStatus[3] = "phlebotomyKit";
          if (testType != 2) {
            subscriptionStatus[1] = "fingerPrickKit";
          }
        }

        if (phlebotomy == 0 && postPKit == 1) {
          if (testType != 2) {
            subscriptionStatus[1] = "fingerPrickKitF";
          }
        }
      }

      const payload = {
        stripeCustomerId: userDetails.customerStripeId,
        stripeToken,
        subscriptionId,
        subscriptionType: "stripe",
        subscriptionStatus: subscriptionStatus[phlebotomyIdSelected],
        isPreBook: true,
      };

      dispatch(updateCollectionMethod(payload)).then(({payload: response}) => {
        if (response.error) {
          setShowError(true);
          setIsSubmitting(false);
          return;
        }

        setIsSuccessfully(true);
        setIsSubmitting(false);
        setSuccessMessage("Your order has been updated successfully");
        activateCountdown();

        const {phlebotomy, phlebotomyClinic, postPKit, subquentialAmounts, kitStatus} = response?.subscriptionUpdated;

        const newPreBookArray = preBookOrders.map(preBook => {
          if (preBook.subscriptionId == order.subscriptionId) {
            return {
              ...preBook,
              subquentialAmounts,
              phlebotomy,
              phlebotomyClinic,
              postPKit,
              kitStatus,
            };
          }
          return preBook;
        });

        setTimeout(() => {
          handleCloseModal && handleCloseModal();
          setIsSuccessfully(false);
          dispatch(updatePreBookOrders(newPreBookArray));
        }, SECONDS_TO_REDIRECT * 1000);
      });
    } else {
      setShowError(true);
      setIsSubmitting(false);
    }
  };

  const buttonCopy = useMemo(() => {
    if (action === "pre-book") {
      if (!isSuccessfully && !testPrice) return "Free";
      if (!isSuccessfully && testPrice) return "Submit";
      if (isSuccessfully) return "Scheduled!";
    }

    if (action === "updateDispatchDate") {
      if (!isSuccessfully) return "Submit";
      return "Updated!";
    }

    if (action === "changeCollectionMethod") {
      const totalToPay = parseFloat(order.subquentialAmounts);
      const totalToPayDisplay = formatNumber(totalToPay);

      if (!isSuccessfully) return "Submit";
      return "Updated!";
    }
  }, [isSuccessfully, order.subquentialAmounts, testPrice, action]);

  const modalTitle = useMemo(() => {
    if (action == "pre-book") {
      const totalToPay = parseFloat(testPrice);
      const totalToPayDisplay = formatNumber(totalToPay);

      return `£${totalToPayDisplay}`;
    }

    if (action == "updateDispatchDate") {
      return "Update Dispatch Date";
    }

    if (action == "changeCollectionMethod") {
      const totalToPay = parseFloat(order.subquentialAmounts);
      const totalToPayDisplay = formatNumber(totalToPay);

      return "Change collection method";
    }
  }, [action, order.subquentialAmounts]);

  return (
    <>
      <form onSubmit={handleSubmit} className="row">
        <RenderIf isTrue={!isSuccessfully}>
          <div className="col-12 text-center">
            <h2 className="mb-3">{modalTitle}</h2>
          </div>
          <Box variant="secondary" className="p-3 mb-4" showShadow={false}>
            <p className="mb-0 lh-base">
              Please enter your card details. You will not charged until the day you have selected. If you have credit
              when this order is dispatched this will be automatically deducted.
            </p>
          </Box>
          <PaymentElement id="payment-element" onChange={handleDisabledButton} />
        </RenderIf>

        <RenderIf isTrue={showError && !isSuccessfully}>
          <div className="col-12 mt-3">
            <Alert variant="danger" className=" d-flex gap-2">
              <div className="col-auto pt-1">
                <FontAwesomeIcon icon={faCircleXmark} className="fa-fw" size="lg" />
              </div>
              <div className="col">
                <p className="mb-0 lh-base">
                  Something went wrong, please try again. If the problem persist, please contact us at{" "}
                  <a href="mailto:contactus@forthwithlife.co.uk">contactus@forthwithlife.co.uk</a>
                </p>
              </div>
            </Alert>
          </div>
        </RenderIf>

        <RenderIf isTrue={isSuccessfully}>
          <div className="col-12 text-center">
            <h2 className="mb-3">Congratulations!</h2>
          </div>
          <div className="col-12 mt-3">
            <h1 className={`text-center ${styles.successIcon}`}>{emojis.success}</h1>
            <Alert variant="text-success" className="text-center gap-2" showShadow={false}>
              <p className="my-3 lh-base">{successMessage}</p>
            </Alert>
            <small className="d-block text-center">Closing window in {secondsRemaining} seconds.</small>
          </div>
        </RenderIf>
        <div className="col-12 mt-4">
          <Button
            variant={isSuccessfully ? "success" : "primary"}
            className="py-3 w-100"
            disabled={isSubmitting || isDisabled}
            isLoading={isSubmitting}
            loadingText="Loading..."
            type={isSuccessfully ? "button" : "submit"}
            onClick={isSuccessfully ? () => {} : handleSubmit}>
            {buttonCopy}
          </Button>
        </div>
        <div className="col-12 mt-3">
          <Button variant="link-default" className="w-100" onClick={handleBack}>
            Go back
          </Button>
        </div>
      </form>
    </>
  );
}
